import React from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import NotActiveAccount from '../no-auth/NotActiveAccount';
import { getAccountBlockReason } from '../../lib/accountAccess';

const ProtectedRoute = ({
    children,
    roles = [],
    permissions = [],
    denyPermissions = [],
}) => {
    const { userProfile, company, loading } = useAuth();
    const { companySlug } = useParams();
    const location = useLocation();

    // Show a skeleton loading screen while auth state or profile is being determined
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-zinc-900 p-8">
                <div className="w-full mx-auto space-y-8">
                    {/* Header Skeleton */}
                    <div className="bg-white dark:bg-zinc-800 rounded-2xl p-8 border border-gray-200 dark:border-zinc-700">
                        <div className="flex items-center gap-3 mb-4">
                            <Skeleton circle width={48} height={48} />
                            <div className="flex-1">
                                <Skeleton width={300} height={32} className="mb-2" />
                                <Skeleton width={200} height={16} />
                            </div>
                        </div>
                        <div className="flex items-center gap-3 mt-6">
                            <Skeleton width={100} height={36} borderRadius={8} />
                            <Skeleton width={100} height={36} borderRadius={8} />
                            <Skeleton width={150} height={36} borderRadius={8} />
                        </div>
                    </div>

                    {/* Stats Grid Skeleton */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[...Array(4)].map((_, index) => (
                            <div key={index} className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                                <div className="flex items-center justify-between mb-4">
                                    <Skeleton circle width={48} height={48} />
                                    <Skeleton width={50} height={24} borderRadius={8} />
                                </div>
                                <Skeleton width={120} height={16} className="mb-2" />
                                <Skeleton width={80} height={36} className="mb-2" />
                                <Skeleton width={140} height={12} />
                            </div>
                        ))}
                    </div>

                    {/* Content Skeleton */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {[...Array(3)].map((_, index) => (
                            <div key={index} className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                                <Skeleton width={150} height={24} className="mb-4" />
                                <Skeleton count={3} height={16} className="mb-2" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Not authenticated - redirect to login
    if (!userProfile || !company) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Company subscription blocked, or the user's own status isn't active
    const blockReason = getAccountBlockReason({ userProfile, company });
    if (blockReason) {
        return <NotActiveAccount user={userProfile} company={company} reason={blockReason} />;
    }

    // Wrong company slug - redirect to correct one
    if (companySlug && companySlug !== company.slug) {
        return <Navigate to={`/${company.slug}/dashboard`} replace />;
    }

    // Role-based restriction
    if (roles.length > 0 && !roles.includes(userProfile.role)) {
        return <Navigate to={`/${company.slug}/dashboard`} replace />;
    }

    const userPermissions = userProfile?.permissions || [];

    const hasRequiredPermission =
        permissions.length === 0 ||
        permissions.some((permission) => userPermissions.includes(permission)) ||
        !userPermissions.some(p =>
            permissions.includes(p) || denyPermissions.includes(p)
        );

    const hasDeniedPermission =
        denyPermissions.length > 0 &&
        denyPermissions.some((permission) => userPermissions.includes(permission));

    if (!hasRequiredPermission || hasDeniedPermission) {
        return <Navigate to={`/${company.slug}/dashboard`} replace />;
    }

    // All good — show protected content
    return children;
};

export default ProtectedRoute;
